import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import InputField from "@/components/forms/input_field";
import SelectField from "@/components/forms/select_field";
import CityCombobox from "@/components/forms/city_combobox";
import ButtonMain from "@/components/buttons/button_main";
import ButtonGhost from "@/components/buttons/button_ghost";
import { useMarket } from "@/context/LocationContext";

const BED_OPTIONS = [
  { value: "any", label: "Any beds" },
  { value: "1", label: "1 plus" },
  { value: "2", label: "2 plus" },
  { value: "3", label: "3 plus" },
];

export default function FilterSheet({ open, onClose, filters, onApply }) {
  const { stayType, setStayType, market } = useMarket();
  const [draft, setDraft] = useState(filters || {});
  const [stay, setStay] = useState(stayType);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  // Re-seed the stay pills AND the draft from the live applied values each
  // time the sheet opens. WHY: dismissing the sheet without Apply used to
  // leave abandoned edits in local state, so reopening showed filters that
  // were never actually applied to the deck.
  useEffect(() => {
    if (open) {
      setStay(stayType);
      setDraft(filters || {});
    }
  }, [open, stayType, filters]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-night-surface p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-ambient sm:max-h-none sm:rounded-3xl sm:pb-6"
            initial={{ y: 80 }}
            animate={{ y: 0 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-5 flex items-center justify-between">
              <h3 className="text-lg font-semibold tracking-tight text-night-text">Filters</h3>
              <button
                type="button"
                aria-label="Close filters"
                onClick={onClose}
                className="rounded-full p-2 text-night-sub transition hover:bg-night-tint hover:text-night-text"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="mb-2 text-sm font-medium text-night-text">Stay type</p>
                <div className="flex gap-3">
                  <Toggle label="Long term" on={stay === "long"} onToggle={() => setStay("long")} />
                  <Toggle label="Short stay" on={stay === "short"} onToggle={() => setStay("short")} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <InputField label="Min price" type="number" placeholder="1000" value={draft.minPrice || ""} onChange={(e) => set("minPrice", e.target.value)} />
                <InputField label="Max price" type="number" placeholder="3500" value={draft.maxPrice || ""} onChange={(e) => set("maxPrice", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <SelectField label="Beds" options={BED_OPTIONS} value={draft.beds || "any"} onChange={(e) => set("beds", e.target.value)} />
                <CityCombobox
                  label="City"
                  provinceCode={market?.province}
                  anyLabel="Any city"
                  value={!draft.city || draft.city === "any" ? "" : draft.city}
                  onChange={(c) => set("city", c || "any")}
                />
              </div>
              <div className="flex gap-3">
                <Toggle label="Pets allowed" on={!!draft.pets} onToggle={() => set("pets", !draft.pets)} />
                <Toggle label="Furnished" on={!!draft.furnished} onToggle={() => set("furnished", !draft.furnished)} />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <ButtonGhost full onClick={() => { setDraft({}); setStay("long"); setStayType("long"); onApply({}); onClose(); }}>Clear all</ButtonGhost>
              <ButtonMain full onClick={() => { setStayType(stay); onApply(draft); onClose(); }}>Apply filters</ButtonMain>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Toggle({ label, on, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`flex-1 rounded-lg border px-4 py-2.5 text-sm font-medium transition ${
        on
          ? "border-brand-primary bg-night-tint text-night-text"
          : "border-night-line bg-night text-night-sub hover:border-brand-primary hover:text-night-text"
      }`}
    >
      {label}
    </button>
  );
}
